import React     from 'react'; 

import Newspaper  from '../components/Newspaper.jsx';
import Newspaper_4_col     from '../components/Newspaper_4_col.jsx';
import Paper      from '../components/Paper.jsx';
import portrait   from './portrait.module.css';
import landscape  from './how_it_works.module.css';
import {Context}  from '../services/context.js'
import { Helmet } from 'react-helmet';
import classNames from 'classnames' 
import cx         from 'classnames';

import block_security_generation from '../pictures/Block Security Generation.avif';
import seal  from '../pictures/Seal-before-manipulation.avif';
import pic_3 from '../pictures/Two Chains.avif';
import pic_4 from '../pictures/Blockchain Foldback_Front of chain.avif';
import pic_5 from '../pictures/Blockchain Paper Diagrams-01.avif';
import pic_6 from '../pictures/Block encryption with modifier.avif';
import pic_7 from '../pictures/Block decryption with modifier.avif';
import pic_8 from '../pictures/Block Structure.avif';
import pic_old_chain from '../pictures/Old Blockchain.avif';
  
 
const cx_header     = cx({[portrait.header]:  Context.portrait, [landscape.header]: !Context.portrait});
const cx_text       = cx({[portrait.text]:    Context.portrait, [landscape.text]:   !Context.portrait});
const cx_ul         = cx({[portrait.ul]:      Context.portrait, [landscape.ul]:     !Context.portrait});
const cx_li         = cx({[portrait.li]:      Context.portrait, [landscape.li]:     !Context.portrait});
const cx_text_wide  = cx([landscape.text_wide])

const how_it_works = [
  {
    header: (
      <p className={cx_header}>
        Traditional Blockchain Review
      </p>
    )
  },
  {
    text: (
      <p className={cx_text}>
        All blockchains use a hash of a block’s contents, which is fed into the next block to form a chain. Any modification of a block changes its hash and those of all subsequent blocks. However, if someone alters a block, recalculates all following hashes, and then claims their chain is legitimate, security is compromised. Current solutions rely on heavy computation and many independent network nodes, making blockchains slow, expensive, and globally dispersed. Another overlooked factor is storage: replicating the chain across thousands of nodes multiplies storage costs, ultimately borne by users in transaction fees.
      </p>
    )
  }
];

const better_chain = [
  {
    header: (
      <p className={cx_header}>
        A Better Chain
      </p>
    )
  },
  {
    text: (
      <p className={cx_text}>
        The <b>Back‑Step Blockchain</b> secures the chain using a different mechanism—lightning-fast, cost‑effective, and without many nodes. Each block includes two extra values generated cryptographically. These values form a reverse chain: you can compute a block’s predecessor but not its successor, linking every block back to the first. No one can predict future values, preventing forged blocks at the chain’s end. One of these values—the seal—guards against internal modifications. Both the back‑step value and forward hash are generated quickly in fixed cycles, making disk writes the primary throughput limiter. See our white papers under <b>Publications</b> for detailed diagrams.
      </p>
    )
  }
];

const chain_on_chain = [
  {
    header: (
      <p className={cx_header}>
        Chain‑on‑Chain<br />
        Parent/Child Mode<br />
        Virtual Chains
      </p>
    )
  },
  {
    text: (
      <p className={cx_text}>
        Back‑Step Blockchain supports variable block sizes up to 100 MB. Each block’s size is defined by the application—no side‑car files required. Initially, we used fixed‑size blocks of ~1 KB payload, which was wasteful for many use cases. With variable sizes, users store payloads from 16 bytes to 100 MB on‑chain. You can also embed entire existing chains as payload. These “child” chains disable their own security and leverage Back‑Step’s performance. A proof‑of‑work chain, for example, can eliminate its 10‑minute mining delay and Merkle hashing, processing each transaction in its own block. Multiple child chains run in parallel, isolating each user’s data with unique cryptographic keys on the parent chain.
      </p>
    )
  }
];

const security_by_chip = [
  {
    header: (
      <p className={cx_header}>
        Security by Chip
      </p>
    )
  },
  {
    text: (
      <p className={cx_text}>
        The core of Back‑Step Blockchain is a generator inside a secured chip containing secret keys—similar to those in credit cards or government ID cards. This hardware‑based approach offers top‑tier security. Future chip versions will add payload encryption and accelerated hashing. Even the smallest implementation can process over 1,000 blocks per second; high‑end chips will reach millions per second. At 2 GHz, one cycle is 0.5 ns, theoretically allowing up to 100 million blocks per second.
      </p>
    )
  }
];

const seal_momu = [
  {
    header: (
      <p className={cx_header}>
        Seal and MoMu for Security
      </p>
    )
  },
  {
    text: (
      <p className={cx_text}>
        The seal is a security value that cannot be forged, even by the generator’s owner. It runs parallel to the back‑step chain but uses independent values. To prevent backward derivation, the seal is one‑way encrypted after an XOR operation. The MoMu value is calculated by multiplying the previous block’s MoMu by the current block’s payload hash modulo a large prime. MoMu synchronizes payload clusters even if blocks arrive out of sequence.
      </p>
    )
  }
];

const do_we_need_networks = [
  {
    header: (
      <p className={cx_header}>
        Do We Need Networks?
      </p>
    )
  },
  {
    text: (
      <p className={cx_text}>
        It depends. In embedded applications—like an ID card or passport—only a local interface (often wireless) is needed. For server deployments, use 3–5 units in a redundant cluster with high‑speed connections. Some use cases may require chains per country, region, or city, linking servers accordingly. Standard applications receive block inputs over the internet. In isolated environments—such as avionics black boxes—no network is required; block authenticity is ensured by the back‑step mechanism. Large node clusters of traditional blockchains are obsolete.
      </p>
    )
  }
];

const redundancy = [
  {
    header: (
      <p className={cx_header}>
        How Does Redundancy Work?
      </p>
    )
  },
  {
    text: (
      <p className={cx_text}>
        Redundancy provides high availability and robust security. Each Back‑Step chain communicates with others in the cluster. Users are assigned to a cluster for load distribution. Each chain processes its payload and replicates encrypted blocks to peers. Since each chain uses distinct cryptographic keys, back‑step values, and seals, even identical payloads yield unique blocks per chain. Unencrypted payloads remain identical across all cluster chains.
      </p>
    )
  }
];

const data_encryption = [
  {
    header: (
      <p className={cx_header}>
        Encryption for Your Data
      </p>
    )
  },
  {
    text: (
      <p className={cx_text}>
        Encrypted data appears on‑chain for all to see, but only you can decrypt it. To defend against advanced cryptanalysis, we implement two AES‑based layers: first, encrypt 16‑byte data units in CBC mode with a unique 256‑bit key; then apply an XOR modifier derived from the data and re‑encrypt units in reverse order with a separate session key. This backward encryption can use extra rounds and variable S‑Boxes for added strength. These techniques exceed typical library implementations and justify the extra fee for privacy blocks.
      </p>
    )
  }
];
  

  export class HowItWorks extends React.Component  
  { 

      //------------------------------------------------------------------
      //  Constructor
      //------------------------------------------------------------------
      constructor(props) 
      {
        super(props);
        this.component= "";
        this.state = {content_disabled: false}
      }  
      
  
      componentDidMount()
      {
        // console.log('Component did mount in About Us');
        //this.forceUpdate();
        window.scrollTo({top:0, left: 0, behavious: 'smooth'});
      };
      
      //------------------------------------------------------------------
      //
      //------------------------------------------------------------------
      changeVisible(mode)
      {
        this.setState({content_disabled:mode})
      }

      //---------------------------------------------------------------------------
      //  Prepare for portrait devices first
      //---------------------------------------------------------------------------
      render()
      { 
        if (Context.portrait)
        {
            this.component = 
            <div className={portrait.backdrop}>

              <Helmet>
                <title>How Back-Step Blockchain works</title>
                <meta name="description" content="Detailed explanation of the invention with diagrams and flowcharts" />
                <meta charset="utf-8" />
              </Helmet>

    
              <div className={portrait.title}>
              How it works...
              </div>

                      {/* How does Back-Step work*/}
                      <div > 
                      < Paper textLines = {how_it_works}/> 
                      </div>


                      <div className={portrait.image}>
                        <p className={cx_text}>Standard chain simplified</p>
                        <img src={pic_old_chain} width='100' alt="Picture of traditional blockchain" />
                      </div>


                      <div > 
                      < Paper textLines = {better_chain}/> 
                      </div>


                      <div className={portrait.image}>
                        <p className={cx_text}>Hash and Back-Step value flow into different directions</p>
                        <img src={pic_3} width='100' alt="pic_introduction" />
                      </div>

                      <div className={portrait.image}>
                        <p className={cx_text}>Feedback link of the first Back-Step value to the hash chain</p>
                        <img src={pic_4} width='100' alt="pic_introduction" />
                      </div>
  
                      <div className={portrait.image}>
                        <p className={cx_text}>The basic Back-Step idea which lead to the patent applications</p>
                        <img src={pic_5} width='100' alt="pic_introduction" />
                      </div>

                      {/* Chain on chain*/}
                      <div className={cx_text}>
                        <Paper  textLines = {chain_on_chain}/>
                      </div>

                      <div className={portrait.image}>
                        <p className={cx_text}>Diagram with payload hash and block hash</p>
                        <img src={block_security_generation} width='250' alt="pic_introduction" />
                      </div>
                      
                    
                      {/* Security by chip*/}
                      <div >
                        <Paper  textLines = {security_by_chip }/>
                      </div>

                      <div className={portrait.image}>
                        <p className={cx_text}>Block Structure and processing</p>
                        <img src={pic_8} width='40' alt="pic_introduction" />
                      </div>


                      {/* Do we need networks*/}
                      <div>
                        <Paper  textLines = {do_we_need_networks}/>
                      </div>


                      {/* How does redundancy work*/}
                      <div> 
                        <Paper  textLines = {redundancy}/>
                      </div>

                      {/*Encryption for your data*/}
                      <div>
                        <Paper textLines = {data_encryption}/>
                      </div>

                      <div  className={portrait.image}>
                        <img src={pic_6}  width='250' alt="encryption concept" />
                    </div>
              
                    <div  className={portrait.image}>
                      <img src={pic_7}  width='250' alt="decryption concept" />
                    </div>

                  </div>
               {/*} </div> */}          
        }
        else
        //---------------------------------------------------------------------------
        //  Prepare for Landscape devices herefirst
        //---------------------------------------------------------------------------
        {
          this.component = 
          <div className={landscape.backdrop} onMouseDown={()=>this.changeVisible(true)} onMouseUp={()=>this.changeVisible(false)} onContextMenu={(e) => e.preventDefault()}>

              <Helmet>
                <title>How Back-Step Blockchain works</title>
                <meta name="description" content="Detailed explanation of the invention with diagrams and flowcharts" />
                <meta charset="utf-8" />
              </Helmet>

            {! this.state.content_disabled &&
              <>
              <div className={landscape.title_location}>
                <div className={landscape.title_text}> 
                  <p> Technical Description How it Works </p>
                </div>
              </div>

              <div className={landscape.container}>

                {/* How do traditional blockchains work*/}
                <div className={classNames(cx_text)}> 
                  < Newspaper textLines = {how_it_works}/> 
                </div>


              <div className={landscape.image_right}>
                        <p className={cx_text}>Standard chain simplified</p>
                        <img src={pic_old_chain} width='100' alt="Picture of traditional blockchain" />
                      </div>


              {/* Better Chain*/}
                <div className={classNames(cx_text)}> 
                  < Newspaper textLines = {better_chain}/> 
                </div>     
                
                <div className={landscape.image_right}>
                  <p>The basic Back-Step idea which lead to the patent applications</p>
                  <img src={pic_3} width='100' alt="pic_introduction" />
                </div>


                 <div className={landscape.image_left}>
                  <p>Feedback link of the first Back-Step value to the hash chain </p>
                  <img src={pic_4} width='100' alt="pic_introduction" />
                </div>

                <div className={landscape.image_right}>
                  <p>Insertion of the Back-Step value into the chain</p>
                  <img src={pic_5} width='100' alt="pic_introduction" />
                </div>


                {/* Seal and MoMu for security*/}
                <div className={cx_text}>
                  <Newspaper  textLines = {seal_momu}/>
                </div>


                <div className={landscape.image_right}>
                  <p>The effect of the seal</p>
                  <img src={seal} width='100' alt="seal protection" />
                </div>



                {/* Security by chip*/}
                <div className={cx_text}>
                  <Newspaper  textLines = {security_by_chip}/>
                </div>


                <div className={landscape.image_right}>
                  <img src={block_security_generation} width='250' alt="block_security_generation"/>
                </div>
              

               {/* Chain on chain*/}
               <div className={cx_text}>
                  <Newspaper  textLines = {chain_on_chain}/>
                </div>

                 
              
   
                <div className={landscape.image_right}>
                  <p>Block Structure and processing</p>
                  <img src={pic_8} width='40' alt="pic_introduction" />
                </div>


                {/* Do we need networks*/}
                <div className={cx_text}>
                  <Newspaper  textLines = {do_we_need_networks}/>
                </div>


                {/* How does redundancy work*/}
                <div className={landscape.item_right}>
                  <Newspaper  textLines = {redundancy}/>
                </div>


                {/*Encryption for your data*/}
                <div className={landscape.item_wide}>
                  <Newspaper_4_col  textLines = {data_encryption}/>
                </div>

                <div  className={landscape.item_wide}>
                  <img src={pic_6} className={landscape.rounded_img} style={{ width: '60%' }} alt="encryption concept" />
                </div>
        
                <div  className={landscape.item_wide}>
                  <img src={pic_7} className={landscape.rounded_img} style={{ width: '60%' }} alt="decryption concept" />
                </div>
                
             </div> 
             </>
          }   
        </div>

        }
     
        return this.component ;
      }
  
}